interface AuthDeps {
  isStub: boolean;
  signOut: () => Promise<void>;
  fetchAuthSession: () => Promise<{ tokens?: { idToken?: { toString(): string } } }>;
}

interface ApiErrorBody {
  error?: string;
  reason?: string;
}

/**
 * sessionStorage key carrying the reason a session ended. The 401 handler
 * does a hard `location.href` reload, so React Router state is lost — this
 * key bridges the gap so the LoginPage can render a contextual banner.
 *
 * Backend contract (HH-200 Unit 28): on idle expiry user-api / admin-api
 * return 401 with `{"error":"session_expired","reason":"idle_timeout"}`.
 *
 * Internal — not exported via the package barrel; callers go through
 * `consumeSessionExpiredReason()` so the read-and-clear contract isn't
 * bypassed.
 */
const SESSION_EXPIRED_REASON_KEY = "hiyaSessionExpiredReason";

// Module-scope cache: `consume` performs a destructive read of
// sessionStorage but React 18 Strict Mode (and any double-mount during
// SPA lifecycle) re-invokes the calling component, so a naive read would
// return the value once and `null` thereafter. Caching the first read at
// module scope keeps the helper idempotent for the lifetime of the page
// (a real navigation / full reload starts a fresh module instance).
let cachedReason: string | null | undefined;

export function createAuthHelpers({ isStub, signOut, fetchAuthSession }: AuthDeps) {
  let signingOut = false;

  async function handleUnauthorized(response?: Response): Promise<never> {
    if (response) {
      try {
        const body = (await response.clone().json()) as ApiErrorBody;
        if (body?.error === "session_expired") {
          globalThis.sessionStorage?.setItem(
            SESSION_EXPIRED_REASON_KEY,
            body.reason ?? "unknown",
          );
        }
      } catch {
        // body absent or not JSON — treat as opaque 401, keep generic banner.
      }
    }
    if (!signingOut) {
      signingOut = true;
      await signOut().catch(() => {});
      globalThis.location.href = "/login";
    }
    throw new Error("Session expired");
  }

  async function getAuthHeaders(): Promise<HeadersInit> {
    if (isStub) {
      return { "Content-Type": "application/json" };
    }
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString() ?? "";
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }

  return { handleUnauthorized, getAuthHeaders };
}

/**
 * Read-and-clear the session-expired reason set by `handleUnauthorized`.
 * Returns null when no reason was stashed (i.e. user navigated to /login
 * organically). Safe to call from a React component render or effect:
 * the first call performs the destructive read, subsequent calls in the
 * same page lifetime return the cached value.
 */
export function consumeSessionExpiredReason(): string | null {
  if (cachedReason !== undefined) {
    return cachedReason;
  }
  const reason = globalThis.sessionStorage?.getItem(SESSION_EXPIRED_REASON_KEY) ?? null;
  if (reason !== null) {
    globalThis.sessionStorage?.removeItem(SESSION_EXPIRED_REASON_KEY);
  }
  cachedReason = reason;
  return reason;
}

/**
 * Test-only escape hatch: forget the cached read so the next call hits
 * sessionStorage again. Production code never needs this — a real page
 * navigation discards the module instance.
 */
export function _resetSessionExpiredCacheForTests(): void {
  cachedReason = undefined;
}
